if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          thumbDots: this.querySelector('[id^="GalleryThumbDots"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) {
          // Even if no thumbnails, we might need dynamic height if it's a slider (e.g. mobile)
          if (this.elements.viewer && this.elements.viewer.slider) {
            this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 50));

            // Initial resize
            const activeMedia = this.elements.viewer.querySelector('.is-active');
            if (activeMedia) this.adjustMediaHeight(activeMedia);
          }
          return;
        }

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 50));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        const initialActiveThumbnail = this.elements.thumbnails.querySelector('button[aria-current="true"]')?.closest('[data-target]');
        if (initialActiveThumbnail) this.setActiveThumbnailDot(initialActiveThumbnail.dataset.mediaPosition);
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

        // Initial resize for slider
        if (this.elements.viewer.slider) {
          const activeMedia = this.elements.viewer.querySelector('.is-active');
          if (activeMedia) this.adjustMediaHeight(activeMedia);
        }
      }

      onSlideChanged(event) {
        const currentElement = event.detail.currentElement;
        this.adjustMediaHeight(currentElement);

        if (!this.elements.thumbnails) return;

        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      adjustMediaHeight(activeMedia) {
        if (!this.elements.viewer.slider || !activeMedia) return;

        // Disable on desktop to prevent layout issues (overlap)
        if (window.matchMedia('(min-width: 750px)').matches) {
          this.elements.viewer.style.height = '';
          return;
        }

        // Use a small timeout to ensure layout is settled
        window.requestAnimationFrame(() => {
          // Use offsetHeight of the slide itself to capture full height + content
          // This prevents the container from being too short and cutting off content/causing overlap
          const height = activeMedia.offsetHeight;
          if (height > 0) {
            this.elements.viewer.style.transition = 'height 0.3s ease';
            this.elements.viewer.style.height = `${height}px`;
          }
        });
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        // Resize on manual selection too
        this.adjustMediaHeight(activeMedia);

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        this.setActiveThumbnailDot(thumbnail.dataset.mediaPosition);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      setActiveThumbnailDot(position) {
        if (!this.elements.thumbDots || !position) return;

        this.elements.thumbDots
          .querySelectorAll('.thumbnail-position-dot')
          .forEach((dot) => dot.removeAttribute('aria-current'));
        const activeDot = this.elements.thumbDots.querySelector(`[data-media-position="${position}"]`);
        if (activeDot) activeDot.setAttribute('aria-current', true);
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
